import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useLogger } from 'Hooks';

import * as actions from 'actions';
import { PutEventsInputEvent } from '../EventsSynchronizer';
import Config from '../../config';

interface SocketAPI {
  send: {
    shoppingCartEvent: (event: PutEventsInputEvent) => void;
    socialTableEvent: (event: PutEventsInputEvent) => void;
  };
  socketState: SOCKET_STATE | null;
}

export enum SOCKET_STATE {
  CONNECTING = 'CONNECTING',
  OPEN = 'OPEN',
  CLOSING = 'CLOSING',
  CLOSED = 'CLOSED',
  ERROR = 'ERROR',
}

const Context = React.createContext<SocketAPI>({
  send: {
    shoppingCartEvent: () => {},
    socialTableEvent: () => {},
  },
  socketState: null,
});

const URL = Config.SOCKET_URL;

const createSocket = () => new WebSocket(URL);

const initialSocket = createSocket();

type SocketAction =
  | 'put-shopping-cart-event'
  | 'put-social-table-event'
  | 'ping'
  | 'broadcast-venue-shopping-cart'
  | 'broadcast-venue-social-table';

const SocketProvider: React.FunctionComponent = ({ children }) => {
  const socket = useRef<WebSocket | null>(null);
  const logger = useLogger('socket-manager');
  const dispatch = useDispatch();
  const venueId = useSelector((state) => state?.venue?.id);
  const cartId = useSelector((state) => state.cartId);
  const tableId = useSelector((state) => state.tableId);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [socketState, setSocketState] = useState<SOCKET_STATE>(SOCKET_STATE.CONNECTING);

  useEffect(() => {
    socket.current = createSocket();
    setRetryAttempt(1);

    // Reload socket when the user leaves and comes back
    /*     function handleVisibilityChange() {
      logger.debug('visibility change:' + document.visibilityState);
      reconnect();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange, false);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange, false);
    }; */
  }, []);

  /* ==================================
      SEND HANDLERS
     ================================== */

  function sendMessage(action: SocketAction, data?: object) {
    if (!socket.current) {
      logger.error('Socket is not initialized');
      return;
    }
    socket.current.send(
      JSON.stringify({
        action,
        data,
      }),
    );
  }

  /**
   * Sends a shopping cart event to the backend
   */
  const shoppingCartEvent = useCallback((event: PutEventsInputEvent) => {
    sendMessage('put-shopping-cart-event', event);
  }, []);

  /**
   * Sends a social table event to the backend
   */
  const socialTableEvent = useCallback((event: PutEventsInputEvent) => {
    sendMessage('put-social-table-event', event);
  }, []);

  /* ==================================
      LIFECYCLE FUNCTIONS
     ================================== */

  function reconnect() {
    // Create a new socket instance
    socket.current = createSocket();

    // And trigger a re-run of this effect
    setRetryAttempt((r) => r + 1);
  }

  function broadcastVenue() {
    if (tableId) {
      sendMessage('broadcast-venue-social-table', { tableId });
    }

    // Let the shopping cart service know we want shopping cart events
    sendMessage('broadcast-venue-shopping-cart', { cartId });
  }

  /* ==================================
      SOCKET STATE HANDLING
     ================================== */

  useEffect(() => {
    if (!socket.current) {
      return;
    }
    setSocketState(SOCKET_STATE.CONNECTING);

    socket.current.addEventListener('open', () => {
      /**
       * Ping the socket once on boot to ensure theres a connection
       */
      sendMessage('ping');

      setSocketState(SOCKET_STATE.OPEN);
    });

    socket.current.addEventListener('close', (event) => {
      setSocketState(SOCKET_STATE.CLOSED);

      logger.info(`Socket closed. Code: ${event.code}`);

      // Reconnect with a tiny timeout, so we dont blast our backend
      // in case something goes wrong
      setTimeout(reconnect, 1000);
    });

    socket.current.addEventListener('error', () => {
      logger.warn('Socket error, closing...');
      socket.current!.close();
    });

    // Listen for messages
    socket.current.addEventListener('message', function (event) {
      try {
        // If this is our keep-alive pingpong, ignore
        if (event.data === 'pong') {
          return;
        }

        const message = JSON.parse(event.data);
        const { message: errorMessage, domain, payload } = message;
        logger.info('Sending Socket Message', message);

        if (errorMessage) {
          logger.error(errorMessage, {
            message,
          });
          return;
        }

        if (!domain) {
          logger.error('Missing domain in socket message', {
            message,
          });
        }

        switch (domain) {
          case 'SHOPPING_CART_EVENT':
            dispatch(actions.addShoppingCartEvent(payload));
            logger.info('Received shopping cart event', { payload });
            break;
          case 'SOCIAL_TABLE_EVENT':
            dispatch(actions.addSocialTableEvent(payload));
            break;
          case 'EVENT_CONFIRMED':
            logger.info('Event confirmed', { payload });
            break;
          default:
            logger.warn('Unknown socket event domain', {
              message,
            });
        }
      } catch (e: any) {
        logger.warn(e.message, { event });
      }
    });

    /* ==================================
      EFFECTS AND MORE LIFECYCLE
     ================================== */

    /**
     * Ping the socket with regular intervals to keep it alive
     * API Gateway timeout is 10 minutes, but 1 minute feels... better
     */
    const id = setInterval(() => {
      sendMessage('ping');
    }, 60000);

    // Refresh the connection every 90 minutes.
    // AWS API Gateway session timeout is 2 hours but lets have some margin
    const refreshId = setTimeout(() => {
      socket.current!.close();
    }, 60 * 90 * 1000);

    /**
     * Ensure we are always in sync with the actual socket state
     */
    /*   const socketStateCheckId = setInterval(() => {
      const state = socket.current?.readyState;
      if (state === WebSocket.CLOSED) {
        logger.warn('Socket is closed');
        setSocketState(SOCKET_STATE.CLOSED);
      }
      if (state === WebSocket.OPEN) {
        if (socketState !== SOCKET_STATE.OPEN) {
          setSocketState(SOCKET_STATE.OPEN);
        }
      }
      if (state === WebSocket.CONNECTING) {
        logger.debug('Socket is connecting');
        setSocketState(SOCKET_STATE.CONNECTING);
      }
      if (state === WebSocket.CLOSING) {
        logger.debug('Socket is closing');
        setSocketState(SOCKET_STATE.CLOSING);
      }
    }, 1000); */

    return () => {
      clearTimeout(refreshId);
      clearInterval(id);
      //clearInterval(socketStateCheckId);
    };
  }, [retryAttempt]);

  // Always have the socket state as context to all logs
  useEffect(() => {
    logger.setContext({
      socketState,
    });

    logger.info(`Socket state: ${socketState}`);
  }, [socketState]);

  useEffect(() => {
    if (!venueId || socketState !== SOCKET_STATE.OPEN) {
      return;
    }

    // Broadcast our connection to start accepting new events
    broadcastVenue();
  }, [socketState, venueId, cartId, tableId]);

  const api: SocketAPI = {
    send: {
      shoppingCartEvent,
      socialTableEvent,
    },
    socketState,
  };

  return <Context.Provider value={api}>{children}</Context.Provider>;
};

export const useSocket = () => {
  return useContext(Context);
};

export default SocketProvider;
